import React, { useMemo } from 'react'
import { CHART_COLORS, parseToUnixSeconds } from '../../config/charts'
import { FinancialChart } from './FinancialChart'

interface VarianceChartProps {
    data: any[]
    symbol?: string
    isLoading?: boolean
}

export const VarianceChart = React.memo(
    ({ data }: VarianceChartProps) => {
        // Process data to match lightweight-charts format
        const chartData = useMemo(() => {
            if (!data) return []
            // 1. Sort by timestamp strictly via parsing first (to ensure sorting is correct)
            const parsed = data
                .map((item) => ({
                    ...item,
                    unixTime: parseToUnixSeconds(item.timestamp),
                }))
                .filter(
                    (i) => i.unixTime !== null && !Number.isNaN(i.finalVariance) && Number.isFinite(i.finalVariance),
                )

            // Sort by time
            parsed.sort((a, b) => (a.unixTime as number) - (b.unixTime as number))

            // 2. Map to { time, value, color } and deduplicate
            const uniqueData = new Map()

            parsed.forEach((item) => {
                const timeVal = item.unixTime as number

                uniqueData.set(timeVal, {
                    time: timeVal as any,
                    value: item.finalVariance,
                    // Use Centralized Text Colors
                    color: item.finalVariance >= 0 ? CHART_COLORS.TEXT.POSITIVE : CHART_COLORS.TEXT.NEGATIVE,
                })
            })

            return Array.from(uniqueData.values())
        }, [data])

        if (!data || data.length === 0) return null

        return (
            <FinancialChart
                type="Histogram"
                data={chartData}
                height={250}
                title="Final OI% Variance Trend"
                colors={{}}
            />
        )
    },
    (prev, next) => {
        // Shallow compare data length for performance
        if (prev.data === next.data) return true
        if (prev.data?.length === next.data?.length) {
            // Deep check last item only to detect live updates
            const lastPrev = prev.data[prev.data.length - 1]
            const lastNext = next.data[next.data.length - 1]

            if (lastPrev?.timestamp === lastNext?.timestamp && lastPrev?.finalVariance === lastNext?.finalVariance)
                return true
        }
        return false
    },
)
